import { ArrowLeftRight, MapPinned } from 'lucide-react'

import type { BookAiSection } from '@/api/services/book-ai'
import { cn } from '@/lib/utils'

import type { GlossaryEntry } from './book-ai-position'
import { glossaryKindConfig } from './glossary-kinds'

type Props = {
  entry: GlossaryEntry
  /** 登場箇所（章ごと。ネタバレ防止で絞ったもの） */
  occurrences: { section: BookAiSection; count: number; firstKey: number }[]
  /** この人物・用語が関わる関係 */
  relations: { other: string; relation: string }[]
  onNavigate: (key: number) => void
}

/** 人物・用語集の 1 項目（説明・別名・関係・登場箇所） */
export const GlossaryItem = ({ entry, occurrences, relations, onNavigate }: Props) => {
  const config = glossaryKindConfig(entry.kind)
  const latest = entry.notes.at(-1)
  const earlier = entry.notes.slice(0, -1)
  const first = occurrences.at(0)

  return (
    <li className="flex gap-2.5 py-2.5">
      <span className={cn('mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full', config.className)}>
        <config.icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div>
          <p className="text-sm font-semibold wrap-break-word">{entry.name}</p>
          {entry.aliases.length > 0 && (
            <p className="text-xs text-muted-foreground wrap-break-word">別名: {entry.aliases.join('・')}</p>
          )}
        </div>
        {latest && <p className="text-sm leading-relaxed wrap-break-word">{latest.description}</p>}

        {relations.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {relations.map((r) => (
              <span
                key={`${r.other}-${r.relation}`}
                className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
              >
                <ArrowLeftRight className="size-3 text-muted-foreground" />
                {r.other}
                <span className="text-muted-foreground">（{r.relation}）</span>
              </span>
            ))}
          </div>
        )}

        {first && (
          <div className="space-y-1">
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPinned className="size-3" />
              初登場: {first.section.title}
            </p>
            <div className="flex flex-wrap gap-1">
              {occurrences.map(({ section, count, firstKey }) => (
                <button
                  key={section.index}
                  type="button"
                  className="max-w-full truncate rounded-full border px-2 py-0.5 text-xs text-primary"
                  onClick={() => onNavigate(firstKey)}
                >
                  {section.title} ×{count}
                </button>
              ))}
            </div>
          </div>
        )}

        {earlier.length > 0 && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer">これまでの説明（{earlier.length}）</summary>
            <ul className="mt-1 space-y-1 pl-2">
              {earlier.map((note, i) => (
                <li key={i} className="wrap-break-word">
                  <span className="font-medium">{note.sectionTitle}: </span>
                  {note.description}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </li>
  )
}
